import {
  ApolloClient,
  InMemoryCache,
  createHttpLink,
  from,
} from "@apollo/client";
import { onError } from "@apollo/client/link/error";
import { RetryLink } from "@apollo/client/link/retry";
import { API_URL } from "./constants";

// Create HTTP link for GraphQL API
const httpLink = createHttpLink({
  uri: API_URL,
  credentials: "include",
});

// Create retry link for network error recovery
const retryLink = new RetryLink({
  delay: {
    initial: 300,
    max: Infinity,
    jitter: true,
  },
  attempts: {
    max: 3,
    retryIf: (error, _operation) => !!error,
  },
});

// Create error link for centralized error handling
const errorLink = onError(
  ({ graphQLErrors, networkError, operation, forward }) => {
    if (graphQLErrors) {
      graphQLErrors.forEach(({ message, locations, path, extensions }) => {
        console.error(
          `[GraphQL error]: Message: ${message}, Location: ${locations}, Path: ${path}`
        );

        // Handle specific error types
        switch (extensions?.code) {
          case "RESTAURANT_NOT_FOUND":
          case "INVALID_RESTAURANT_ACCESS":
            // Invalid restaurant parameters - redirect to restaurant selection
            console.warn(
              "Invalid restaurant access, redirecting to selection page"
            );
            window.location.href = "/select-restaurant";
            break;
          case "VALIDATION_ERROR":
            console.warn("Validation error:", message);
            break;
          default:
            console.error("Unhandled GraphQL error:", message);
        }
      });
    }

    if (networkError) {
      console.error(`[Network error]: ${networkError}`);

      // Handle specific network errors
      if (networkError.message.includes("Failed to fetch")) {
        console.warn("Network connectivity issue detected");
      }
    }
  }
);

// Create Apollo Client with parameter-based access (no authentication)
export const client = new ApolloClient({
  link: from([errorLink, retryLink, httpLink]),
  cache: new InMemoryCache({
    // Configure cache to handle restaurant-specific data
    typePolicies: {
      Query: {
        fields: {
          // Cache menu data by brandId and pointId
          menu: {
            keyArgs: ["brandId", "pointId"],
          },
          // Cache restaurant data by brandId and pointId
          point: {
            keyArgs: ["id", "brandId"],
          },
          brand: {
            keyArgs: ["id"],
          },
        },
      },
    },
  }),
  // Error policy: return both data and errors for graceful degradation
  defaultOptions: {
    watchQuery: {
      errorPolicy: "all",
    },
    query: {
      errorPolicy: "all",
    },
  },
});

// Export types for parameter-based access
export interface RestaurantParams {
  brandId: string;
  pointId: string;
}
